import React, { useState, useRef, useEffect, useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import axios from 'axios';
import CryptoJS from 'crypto-js';
import { useTranslation } from 'react-i18next';
import styles from './Login.module.scss';
import loginImg from '../../img/loginImg.svg';
import { IinContext } from '../../context/IinContext';
import { config } from '../../config';
import { VerificationCodeInput } from '../../components';
import { getCodeErrorMessage } from '../../utils/verificationCode';
import { NCALayerClient } from 'ncalayer-js-client';
import { AuthTabs, PasswordForm, EmailConfirmation } from '../../components';
import Cookies from 'js-cookie';
 
export const Login = () => {
  const { t, i18n } = useTranslation();
  const [webSocket, setWebSocket] = useState(null);
  const [subjectDn, setSubjectDn] = useState('');
  const [showLogin, setShowLogin] = useState(false);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [activeTab, setActiveTab] = useState('/digital-signature');
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);
  const [email, setEmail] = useState('');
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [showResendButton, setShowResendButton] = useState(false);
  const [resIin, setResIin] = useState('');
  const storageSelectRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [showSessionConfirmation, setShowSessionConfirmation] = useState(false);
  const [currentSecretCode, setCurrentSecretCode] = useState('');
  const [username, setUsername] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [loadingUserData, setLoadingUserData] = useState(false);
  const { setIin, setUserInfo, setPhysPersonFullname, setIinForBin, setOrgName, setFirstName, setLastName, setBin, setIinOrBin } = useContext(IinContext);
  const ncalayerClient = new NCALayerClient();
 
  useEffect(() => {
    ncalayerClient.connect().catch((error) => { 
      alert("На устройстве не запущен NCALayer");
      console.error("Ошибка подключения к NCALayer:", error);
    });
  }, []);
 
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const clientId = urlParams.get('client-id');
    const backurl = urlParams.get('backurl');
 
    if (clientId) {
      sessionStorage.setItem('client-id', clientId);
    }
 
    if (backurl) {
      sessionStorage.setItem('backurl', backurl);
    }
  }, []);

  useEffect(() => {
    const validateExistingSecretCode = async () => {
      const existingSecretCode = Cookies.get('secretCode');
      if (existingSecretCode) {
        try {
          const validationResponse = await axios.post(
            '/kcssobackext/sso/external/validateSecretCode', {
            secretCode: existingSecretCode
          });
          
          if (validationResponse.data === true) {
            setLoadingUserData(true);
            try {
              const usernameResponse = await axios.post(
                `/kcssobackext/sso/external/getUsername/${existingSecretCode}`,
                {},
                { headers: { 'Accept': '*/*' } }
              );
              
              setUsername(usernameResponse.data.fullName);
              if(usernameResponse.data.organizationName) {
                setOrganizationName(usernameResponse.data.organizationName)
              }
              setCurrentSecretCode(existingSecretCode);
            } catch (error) {
              console.error("Ошибка при получении username:", error);
              setUsername('пользователь');
              setCurrentSecretCode(existingSecretCode);
            } finally {
              setLoadingUserData(false); 
              setShowSessionConfirmation(true);
            }
          } else {
            Cookies.remove('secretCode');
          }
        } catch (error) {
          console.error("Ошибка при валидации secretCode:", error);
          Cookies.remove('secretCode');
        }
      }
    };

    validateExistingSecretCode();
  }, []);

  const handleDeclineSession = async () => {
    setLoading(true);
    try {
      const key = CryptoJS.enc.Hex.parse('10c3417e0b38987f3b44528d999a090efd708bcef15d6357c252bcd42b4cf03d');
      const iv = CryptoJS.enc.Hex.parse('315f48391c3d3cc857128287f8457d47');
      const storedIin = localStorage.getItem('iin');
 
      let cryptedIin = resIin ? resIin : storedIin
 
      const encryptedIin = CryptoJS.AES.encrypt(cryptedIin, key, { iv: iv }).toString();

      await axios.post('/kcssobackext/sso/external/deactivateSecretCode', {
        username: encryptedIin
      });
      Cookies.remove('secretCode');
      setShowSessionConfirmation(false);
      setShowLogin(false);
    } catch (error) {
      console.error("Ошибка при деактивации secretCode:", error);
      setErrorMessage("Не удалось завершить сессию");
    } finally {
      setLoading(false);
    }
  };
 
  const handleConfirmSession = async () => {
    await redirectToReferrer(currentSecretCode);
    // if (window.opener) {
    //   console.log("window.opener")
    //   window.opener.postMessage(
    //     { type: 'AUTH_SECRET_CODE', secretCode: currentSecretCode }, 
    //     "*"
    //   );
    //   window.close();
    // } else {
    //   console.error("Не удалось передать данные: родительское окно не найдено.");
    // }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const langParam = urlParams.get('lang');
    
    if (langParam && ['ru', 'kk', 'en'].includes(langParam)) {
      i18n.changeLanguage(langParam);
      localStorage.setItem('preferredLanguage', langParam);
    }
  }, [location.search, i18n]);
 
  const checkIinOrBin = async (iin, subjectDn) => {
    setLoading(true);
    try {
      const bin = extractBIN(subjectDn);
      const iinOrBinParam = bin ? `${bin}${iin}` : iin;
      if(bin) {
        setIin(bin + iin);
      }

      const response = await axios.get(`/kcssobackext/sso/getInfo?iinOrBin=${iinOrBinParam}`);
      if (response.data.exists) {
        if (response.data.emailVerified) {
          sessionStorage.setItem('email', response.data.email);
          sessionStorage.setItem('physPerson', response.data.physPerson);
          setUserInfo(response.data);
          setShowEmailConfirmation(false);
          setShowLogin(true);
        } else {
          setEmail(response.data.email);
          setShowLogin(false);
          setShowEmailConfirmation(true);
        }
      } else {
        navigate('/register');
      }
    } catch (error) {
      console.error(t('error.checkIinBinError'), error);
    }
    setLoading(false);
  };
 
  const getKeyInfoCall = async () => {
    const selectedStorage = storageSelectRef.current.value || NCALayerClient.fileStorageType;
    try {
      const certInfo = await ncalayerClient.getKeyInfo(selectedStorage);
  
      const iin = await fetchIinFromCertificate(certInfo.pem);
  
      if (iin) {
        setIin(iin);
        // setResIin(iin);
        setShowLogin(true);
        checkIinOrBin(iin, certInfo.subjectDn);
      } else {
        console.error("Ошибка: не удалось извлечь ИИН.");
      }
    } catch (error) {
      console.error("Ошибка получения информации о сертификате:", error);
 
      try {
        await ncalayerClient.connect();
  
        const certInfo = await ncalayerClient.getKeyInfo(selectedStorage);
  
        const iin = await fetchIinFromCertificate(certInfo.pem);
  
        if (iin) {
          setShowLogin(true);
          checkIinOrBin(iin, certInfo.subjectDn);
        } else {
          console.error("Ошибка: не удалось извлечь ИИН после переподключения.");
        }
      } catch (reconnectError) {
        console.error("Ошибка переподключения к NCALayer:", reconnectError);
        setErrorMessage("Не удалось подключиться к NCALayer.");
      }
    }
  };
 
  const getKeyInfo = (storageName) => {
    const getKeyInfoRequest = {
      "module": "kz.gov.pki.knca.commonUtils",
      "method": "getKeyInfo",
      "args": [storageName]
    };
    webSocket.send(JSON.stringify(getKeyInfoRequest));
  };

  const extractFirstAndLastName = (commonName) => {
    const parts = commonName.split(' ');
    const lastName = parts[0];
    const firstName = parts[1];

    setFirstName(firstName);
    setLastName(lastName);
    setPhysPersonFullname(commonName);
  }
 
  const handleWebSocketResponse = async (result) => {
    if (result.code === "500") {
      console.log(result.message);
    } else if (result.code === "200") {
      const certNotAfterRaw = result.responseObject.certNotAfter;
      let certNotAfter = new Date(certNotAfterRaw);
 
      if (certNotAfter.toString() === 'Invalid Date') {
        try {
          const timestamp = Number(certNotAfterRaw);
          if (!isNaN(timestamp)) {
            certNotAfter = new Date(timestamp);
          }
        } catch (error) {
          console.error("Error during manual parsing:", error);
        }
      }
 
      if (certNotAfter.toString() === 'Invalid Date') {
        console.error("certNotAfter is invalid:", certNotAfterRaw);
        return;
      }
 
      const currentDate = new Date();
      if (currentDate <= certNotAfter) {
        setSubjectDn(result.responseObject.subjectDn);
 
        const subjectDn = result.responseObject.subjectDn;
        const isLegalEntity = subjectDn.includes('OU=BIN');
 
        let iin, fullName, bin;
        if (isLegalEntity) {
          bin = extractBIN(subjectDn);
          iin = extractIin(subjectDn)
          const orgName = extractO(subjectDn);
          const cn = extractCN(subjectDn);
          const g = extractG(subjectDn);
          setPhysPersonFullname(cn + ' ' + g);
          if (iin && orgName) {
            sessionStorage.setItem('БИН', bin);
            sessionStorage.setItem('name', orgName.replace(/\\/g, ''));
          }
          fullName = orgName.replace(/\\/g, '');
          // setIinForBin(iin);
          // setOrgName(fullName);
          
        } else {
          iin = extractIin(subjectDn);
          const cn = extractCN(subjectDn);
          const g = extractG(subjectDn);

          if (iin && cn) {
            sessionStorage.setItem('ИИН', iin);
            sessionStorage.setItem('name', `${cn} ${g || ''}`);
          }
          fullName = `${cn} ${g || ''}`;
          // setIinForBin('');
          setPhysPersonFullname(cn + ' ' + g);
          setIin(iin); 
        }
        checkIinOrBin(iin, subjectDn);
      } else {
        alert(t('alert.certExpired'));
      }
    }
  };
 
  const extractBIN = (subjectDn) => {
    const match = subjectDn.match(/OU=BIN(\d+)/);
    return match ? match[1] : '';
  };
 
  const extractIin = (responseData) => {
    if (responseData && responseData.signers && responseData.signers.length > 0) {
      const signer = responseData.signers[0];
      if (signer.subject && signer.subject.iin) {
        return signer.subject.iin;
      }
    }
    console.error("Не удалось извлечь ИИН из ответа API");
    return null;
  };
 
  const extractCN = (subjectDn) => {
    const match = subjectDn.match(/CN=([^,]+)/);
    return match ? match[1] : '';
  };
 
  const extractG = (subjectDn) => {
    const match = subjectDn.match(/G=([^,]+)/);
    return match ? match[1] : '';
  };
 
  const extractO = (subjectDn) => {
    const match = subjectDn.match(/O=([^,]+)/);
    return match ? match[1] : '';
  };
 
  const handleLoginSubmit = async (password) => {
    setLoading(true);
    setErrorMessage('');
    
    try {
      const existingSecretCode = Cookies.get('secretCode');
      if (existingSecretCode) {
        try {
          const validationResponse = await axios.post('/kcssobackext/sso/external/validateSecretCode', {
            secretCode: existingSecretCode
          });
          
          if (validationResponse.data === true) {
            await redirectToReferrer(existingSecretCode);
            // if (window.opener) {
            //   window.opener.postMessage(
            //     { type: 'AUTH_SECRET_CODE', secretCode: existingSecretCode }, 
            //     "*"
            //   );
            //   window.close();
            // }
            setLoading(false);
            return;
          } else {
            Cookies.remove('secretCode');
          }
        } catch (error) {
          console.error("Ошибка при валидации secretCode:", error);
          Cookies.remove('secretCode');
        }
      }
  
      const key = CryptoJS.enc.Hex.parse('10c3417e0b38987f3b44528d999a090efd708bcef15d6357c252bcd42b4cf03d');
      const iv = CryptoJS.enc.Hex.parse('315f48391c3d3cc857128287f8457d47');
      const encryptedUsername = CryptoJS.AES.encrypt(resIin, key, { iv: iv }).toString();
      const encryptedPassword = CryptoJS.AES.encrypt(password, key, { iv: iv }).toString();
  
      const clientId = sessionStorage.getItem('client-id');
      const response = await axios.post('/kcssobackext/sso/external/login', {
        username: encryptedUsername,
        password: encryptedPassword,
        clientId: Number(clientId)
      });
  
      if (response.data) {
        Cookies.set('secretCode', response.data, { 
          expires: 0.25,
          domain: window.location.hostname,
          path: '/kclssofront',
          sameSite: 'Lax',
          secure: false 
        });
        await redirectToReferrer(response.data);
      } else {
        throw new Error('Secret code not found in response');
      }
    } catch (error) {
      if (error.response) {
        if (error.response.status == 500 && error.response.data.includes('Bad creds')) {
          setErrorMessage(t('error.invalidPassword'));
        } else if (error.response.status === 401) {
          setErrorMessage(t('error.invalidPassword'));
        } else {
          setErrorMessage(t('error.serverError'));
        }
      } else {
        console.error(t('error.loginFailed'), error);
        setErrorMessage(t('error.serverError'));
      }
    }
    setLoading(false);
  };
 
  const fetchRedirectUrl = async (clientId) => {
    try {
      const response = await axios.get(`/kcssobackext/client/${clientId}`);
      if (response.data && response.data.redirectUrl) {
        return response.data.redirectUrl;
      } else {
        throw new Error("Redirect URL not found in response");
      }
    } catch (error) {
      console.error("Error fetching redirect URL:", error);
      throw error;
    }
  };

  const sendPostMessage = (secretCode, backurl) => {
    if (window.opener) {
      window.opener.postMessage(
        { 
          type: 'AUTH_SECRET_CODE', 
          secretCode: secretCode,
          backurl: backurl
        },
        "*"
      );
      window.close();
    }
  };
 
  const redirectToReferrer = async (secretCode) => {
    const clientId = sessionStorage.getItem('client-id');
    const backurl = sessionStorage.getItem('backurl');
    
    if (!clientId) {
      console.error("Client ID is missing");
      return;
    }
  
    try {
      const validationResponse = await axios.post('/kcssobackext/sso/external/validateSecretCode', {
        secretCode: secretCode
      });
      
      if (validationResponse.data !== true) {
        Cookies.remove('secretCode');
        return;
      }
  
      if (window.opener) {
        sendPostMessage(secretCode, backurl);
        return;
      }
  
      const redirectUrl = await fetchRedirectUrl(clientId);
      const url = new URL(redirectUrl);
      const params = new URLSearchParams(url.search);
      params.append('client-id', clientId);
      
      // if (backurl) {
      //   const separator = url.search ? '&' : '?';
      //   url.href = url.href + separator + `backurl=${backurl}`;
      // }
      
      window.location.href = url.toString();

      // setTimeout(() => {

      //   window.opener?.postMessage({
      //     type: 'AUTH_SECRET_CODE',
      //     secretCode: secretCode
      //   }, url.origin);
      // }, 100);
    } catch (error) {
      console.error("Error during redirection:", error);
      navigate('/');
    }
  };
  
  const sendResetCode = async () => {
    try {
      const response = await axios.post('/kcssobackext/sso/external/sendResetCode', {
        iinOrBin: resIin,
        method: "email"
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.status !== 200) {
        throw new Error(t('forgotPassword.sendCodeError'));
      }
    } catch (error) {
      console.error(t('forgotPassword.sendCodeError'), error);
      setErrorMessage(t('forgotPassword.sendCodeError'));
    }
  };
 
  const handleEmailSubmit = async () => {
    // if (!email || !subjectDn) return;
 
    setShowResendButton(true);
 
    // let iin = extractBIN(subjectDn);
 
    // if (!iin) {
    //   iin = extractIin(subjectDn);
    // }
    
    try {
      const response = await axios.post('/kcssobackext/sso/external/changeEmail', {
        iinOrBin: resIin,
        email: email
      });
 
      if (response.status === 200) {
        setShowCodeInput(true);
        setShowResendButton(false);
      } else {
        alert(t('error.sendCodeError'));
      }
    } catch (error) {
      console.error(t('error.sendCodeError'), error);
      alert(t('error.sendCodeError'));
    }
  };
 
  const cleanCertificate = (certificateBase64) => {
    return certificateBase64
      .replace(/-----BEGIN CERTIFICATE-----/g, '')
      .replace(/-----END CERTIFICATE-----/g, '')
      .replace(/[\r\n]+/g, '')
      .trim();
  };
 
  const fetchIinFromCertificate = async (certificateBase64) => {
    try {
      const response = await axios.post('/kcssobackext/sso/external/validateCert', {
        revocationCheck: ["OCSP"],
        certs: [cleanCertificate(certificateBase64)]
      }, {
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.data && response.data.status === 200) {
        const { error, showLogin } = processCertificateResponse(response.data);
  
        extractFirstAndLastName(response.data.signers[0].subject.commonName);

        setOrgName(response.data.signers[0].subject.organization);
        setIinForBin(response.data.signers[0].subject.iin);
        setBin(response.data.signers[0].subject.bin);

        if (error) {
          setErrorMessage(error);
          return null;
        }
        const iin = extractIin(response.data);
        if(response.data.signers[0].subject.bin) {
          setResIin(response.data.signers[0].subject.bin + iin);
          setIinOrBin(response.data.signers[0].subject.bin + iin);
          localStorage.setItem('iin', response.data.signers[0].subject.bin);
        } else {
          setResIin(iin);
          localStorage.setItem('iin', response.data.signers[0].subject.iin);
          setIinOrBin(response.data.signers[0].subject.iin);
        }
        return iin;
      } else {
        console.error("Ошибка в полученном ответе API:", response.data);
        setErrorMessage("Ошибка обработки сертификата");
        return null;
      }
    } catch (error) {
      console.error("Ошибка запроса на получение ИИН:", error);
      setErrorMessage("Ошибка при проверке сертификата");
      return null;
    }
  };
 
  const processCertificateResponse = (responseData) => {
    if (!responseData || !responseData.signers || responseData.signers.length === 0) {
      alert("Некорректный ответ API");
      return { error: "Ошибка обработки сертификата" };
    }
  
    const signer = responseData.signers[0];
  
    const revoked = signer.revocations?.some(revocation => revocation.revoked === true);
  
    if (signer.valid && revoked) {
      return { showLogin: true };
    }
  
    if (!signer.valid && revoked) {
      alert("Данный ЭЦП сертификат отозван, используйте другой");
      return { error: "Данный ЭЦП сертификат отозван, используйте другой" };
    }
  
    if (!signer.valid && !revoked) {
      alert("Данный ЭЦП сертификат просрочен, используйте другой"); 
      return { error: "Данный ЭЦП сертификат просрочен, используйте другой" };
    }
  
    // if (signer.keyUser && signer.keyUser.includes("ORGANIZATION")) {
    //   alert("Авторизация возможна только для физических лиц"); 
    //   return { error: "Авторизация возможна только для физических лиц" };
    // }
  
    return { showLogin: true };
  };
 
  const handleCodeSubmit = async (code) => {
    setVerificationCode(code);
 
    if (!code) return;
 
    // let iin = extractBIN(subjectDn);
 
    // if (!iin) {
    //   iin = extractIin(subjectDn);
    // }
    
    try {
      const response = await axios.post('/kcssobackext/sso/external/checkVerificationCode', {
        iinOrBin: resIin,
        code: code,
        method: "email"
      });
    
      if (response.data === true) {
        setShowCodeInput(false);
        setShowLogin(true);
        setShowResendButton(false);
        setShowEmailConfirmation(false);
        setShowEmailInput(false);
        setLoading(false);
      } else {
        alert(t('error.invalidCode'));
        setShowResendButton(true);
      }
    } catch (error) {
      if (error.response) {
        const codeErrorMessage = getCodeErrorMessage(error.response.status, error.response.data, t);
        if (codeErrorMessage) {
          alert(codeErrorMessage);
          setShowResendButton(true);
        } else {
          console.error(t('error.checkCodeError'), error);
          alert(t('error.checkCodeError'));
        }
      } 
    }
  };
 
  const handleConfirmEmail = async () => {  
    // let iin = extractBIN(subjectDn);
 
    // if (!iin) {
    //   iin = extractIin(subjectDn);
    // }
    // console.log("ИИН/БИН:", iin);
    
    try {
      const response = await axios.post('/kcssobackext/sso/external/changeEmail', {
        iinOrBin: resIin,
        email: email
      });
 
      if (response.status === 200) {
        setShowCodeInput(true);
        setShowEmailConfirmation(false); 
      } else {
        alert(t('error.sendCodeError'));
      }
    } catch (error) {
      console.error(t('error.sendCodeError'), error);
      alert(t('error.sendCodeError'));
    }
  };
 
  async function connectAndSign() {
    const ncalayerClient = new NCALayerClient();
    try {
      await ncalayerClient.connect();
    } catch (error) {
      alert(`Не удалось подключиться к NCALayer: ${error.toString()}`);
      return;
    }
    
    const documentInBase64 = 'MTEK';
    let base64EncodedSignature;
    try {
      base64EncodedSignature = await ncalayerClient.basicsSignCMS(
        NCALayerClient.basicsStorageAll,
        documentInBase64,
        NCALayerClient.basicsCMSParamsDetached,
        NCALayerClient.basicsSignerSignAny,
      );
      setShowLogin(true)
    } catch (error) {
      if (error.canceledByUser) {
        alert('Действие отменено пользователем.');
      }
      alert(error.toString());
      return;
    }
  
    return base64EncodedSignature;
  }
 
  return (
    <div>
      <Paper classes={{ root: styles.root }}>
        {loading && (
          <div className={styles.loader}>
            <CircularProgress />
          </div>
        )}
        {!loading && !showEmailConfirmation && !showEmailInput && !showCodeInput && (
          <>
            {!showSessionConfirmation && (
              <div className={styles.tab}>
                <AuthTabs activeTab={activeTab} setActiveTab={setActiveTab} />
              </div>
            )}
            {
            showSessionConfirmation ? (
            <div className={styles.sessionConfirmation}>
              {loadingUserData ? (
                <div className={styles.loaderWrapper}>
                  <CircularProgress size={24} />
                </div>
              ) : (
                <>
                  <p style={{ marginBottom: "0" }}>{t('common.continue')} {username}?</p>
                  <br />
                  <p>{organizationName}</p>
                  {loading ? (
                    <div className={styles.loaderWrapper}>
                      <CircularProgress size={24} />
                    </div>
                  ) : (
                    <div className={styles.confirmationButtons}>
                      <Button variant="contained" color="primary" onClick={handleConfirmSession}>
                        {t('common.yes')}
                      </Button>
                      <Button variant="outlined" color="secondary" onClick={handleDeclineSession}>
                        {t('common.no')}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          ) : showLogin ? (
              <div className={styles.loginContainer}>
                <div>
                  <img src={loginImg} alt={t('alt.loginImage')} />
                  <p className={styles.loginTitle}>{t('login.validationSuccess')}</p>
                  <p className={styles.loginLabel}>{t('login.enterPassword')}</p>
                </div>
                <PasswordForm
                  onSubmit={handleLoginSubmit}
                  isLoading={loading}
                  errorMessage={errorMessage}
                  passwordLabel={t('login.password')}
                  submitButtonText={t('login.loginButton')}
                />
              </div>
            ) : (
              <div className={styles.centerContainer}>
                <div style={{ display: "none" }}>
                  <input type="text" value={subjectDn} id="subjectDn" readOnly />
                  <select ref={storageSelectRef} id="storageSelect" className="custom-select">
                    <option value="PKCS12">PKCS12</option>
                  </select>
                </div>
                <Button
                  className={styles.cert}
                  size="large"
                  variant="contained"
                  onClick={getKeyInfoCall}
                >
                  {t('login.selectCertificate')}
                </Button>
              </div>
            )}
          </>
        )}
        {showEmailConfirmation && !showEmailInput && (
          <EmailConfirmation
            email={email}
            onConfirm={handleConfirmEmail}
            onChangeEmail={() => setShowEmailInput(true)}
          />
        )}
        {showEmailInput && !showCodeInput && (
          <div className={styles.emailConfirmationContainer}>
            <h2>{t('emailConfirmation.title')}</h2>
            <p>{t('emailConfirmation.enterEmail')}</p>
            <div className={styles.emailConfirmationContent}>
              <TextField
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                variant="outlined"
                margin="normal"
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleEmailSubmit}
              >
                {t('emailConfirmation.sendCode')}
              </Button>
            </div>
          </div>
        )}
        {showCodeInput && (
          <div className={styles.emailConfirmationContainer}>
            <VerificationCodeInput
              onSubmit={handleCodeSubmit}
              onResend={sendResetCode}
              showResendButton={true}
            />
          </div>
        )}
      </Paper>
      <p style={{ fontSize: "12px", textAlign: "center" }}>{t('footer.disclaimer1')}</p>
      <p style={{ fontSize: "12px", textAlign: "center" }}>{t('footer.disclaimer2')}</p>
    </div>
  );
};